import React, { useState, useEffect, useRef } from 'react';
import heic2any from 'heic2any';
import { sendChatMessage, getChatHistory } from '../services/api';
import { logoutUser } from '../services/authService';

// --- 1. MOBILE PRICING STRIP (New Component) ---
// Placed inside the chat column so it stays fixed under the header
const MobilePricingStrip = () => {
  const prices = [
    { mobileSize: '8 x 10"', price: '£45' },
    { mobileSize: '8 x 12" (A4)', price: '£49' },
    { mobileSize: '16 x 20"', price: '£59' },
    { mobileSize: '20 x 30"', price: '£74' },
  ];

  return (
    <div className="shrink-0 bg-[#F9F9F7] border-b border-stone-200 p-3 block lg:hidden">
      {/* Title */}
      <h3 className="font-serif text-[10px] text-stone-500 uppercase tracking-widest mb-2 ml-1">
        Commission Rates
      </h3>

      {/* Horizontal Row */}
      <div className="flex overflow-x-auto pb-1 gap-6 no-scrollbar">
        {prices.map((item, index) => (
          <div key={index} className="shrink-0 flex flex-col">
            <span className="font-serif text-sm text-stone-900 font-medium leading-none">
              {item.mobileSize}
            </span>
            <span className="font-serif text-sm text-stone-600 italic mt-1 leading-none">
              {item.price}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- 2. DESKTOP PRICING PANEL ---
// Only visible on large screens
const DesktopPricingPanel = () => {
  const prices = [
    { name: 'Small Portrait', size: '8 x 10 inches', price: '£45', desc: 'Perfect for intimate spaces.' },
    { name: 'Standard A4', size: '8 x 12 inches', price: '£49', desc: 'Classic A4 size.' },
    { name: 'Statement Piece', size: '16 x 20 inches', price: '£59', desc: 'Ideal for living areas.' },
    { name: 'Gallery Grand', size: '20 x 30 inches', price: '£74', desc: 'Dominates the room.' },
  ];

  return (
    <div className="hidden lg:block w-72 shrink-0 border-l border-stone-200 bg-[#F9F9F7] p-6">
      <div className="sticky top-6 space-y-8">
        {/* HEADER */}
        <div className="border-b border-stone-300 pb-4">
          <h3 className="font-serif text-lg text-stone-900 tracking-wide">
            Commission Rates
          </h3>
          <p className="text-xs text-stone-500 uppercase tracking-widest mt-1">
            2026 Collection
          </p>
        </div>

        {/* PRICING LIST */}
        <div className="space-y-6 font-serif">
          {prices.map((item, index) => (
            <div key={index} className="group">
               <div className="flex justify-between items-baseline mb-1">
                <span className="text-stone-900 font-medium">{item.name}</span>
                <span className="text-stone-600 font-light italic">{item.price}</span>
              </div>
              <p className="text-xs text-stone-500 leading-relaxed">
                {item.size}<br/>
                <span className="opacity-75">{item.desc}</span>
              </p>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="pt-6 border-t border-stone-300">
          <p className="text-[10px] text-stone-400 leading-normal uppercase tracking-widest">
            * Includes digital verification and certificate of authenticity.
          </p>
        </div>
      </div>
    </div>
  );
};

function ChatbotTab({ user }) {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const loadHistory = async () => {
      const serverHistory = await getChatHistory();

      if (serverHistory && serverHistory.length > 0) {
        setMessages(serverHistory.map(msg => ({
          text: msg.content,
          isBot: msg.role === 'assistant',
          images: msg.images || []
        })));
      } else {
        setMessages([{
          text: "Hi, I'm Marty, your art assistant. \nPlease upload 1-3 photos that depict the object of your masterpiece. \nFor best results try to check the following: \nClear view of face, good lighting, avoid shadows and ideally a high resolution photo. \nSupported formats are JPEG, JPG, PNG, WebP, HEIC, HEIF.",
          isBot: true
        }]);
      }
    };

    loadHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, imagePreviews, isProcessingImages]);

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];

    const validFiles = files.filter(file => {
      const isValidType = validTypes.includes(file.type);
      const isHeicExt = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      const isEmptyType = file.type === "";
      return isValidType || isHeicExt || isEmptyType;
    });

    if (validFiles.length !== files.length) {
      alert('Some files were ignored. Only images (JPG, PNG, HEIC) are allowed.');
    }

    const filesToAdd = validFiles.slice(0, 5 - selectedImages.length);
    if (filesToAdd.length < validFiles.length) {
      alert('Maximum 5 images allowed per message.');
    }

    if (filesToAdd.length === 0) return;

    setIsProcessingImages(true);

    try {
        const newPreviews = [];
        const newOriginalFiles = [];

        for (const file of filesToAdd) {
            newOriginalFiles.push(file);

            const isHeic = file.name.toLowerCase().endsWith('.heic') ||
                           file.name.toLowerCase().endsWith('.heif') ||
                           file.type.includes('heic') ||
                           file.type.includes('heif');

            if (isHeic) {
                try {
                    const convertedBlob = await heic2any({
                        blob: file,
                        toType: "image/jpeg",
                        quality: 0.5
                    });
                    const blobToUse = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                    newPreviews.push(URL.createObjectURL(blobToUse));
                } catch (err) {
                    console.error("HEIC Preview failed:", err);
                    newPreviews.push(URL.createObjectURL(file));
                }
            } else {
                newPreviews.push(URL.createObjectURL(file));
            }
        }

        setSelectedImages(prev => [...prev, ...newOriginalFiles]);
        setImagePreviews(prev => [...prev, ...newPreviews]);

    } catch (error) {
        console.error("Error processing images", error);
    } finally {
        setIsProcessingImages(false);
        e.target.value = '';
    }
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const clearImages = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const sendMessage = async () => {
    if ((!currentMessage.trim() && selectedImages.length === 0) || isProcessingImages) return;

    const imageDataUrls = [...imagePreviews];

    setMessages(prev => [...prev, {
      text: currentMessage,
      isBot: false,
      images: imageDataUrls
    }]);

    const messageText = currentMessage;
    const imagesToSend = [...selectedImages];

    setCurrentMessage("");
    clearImages();
    setIsLoading(true);

    try {
       const base64Images = await Promise.all(
          imagesToSend.map(async (file) => ({
          data: await fileToBase64(file),
          type: file.type || (file.name.toLowerCase().endsWith('.heif') ? 'image/heif' : 'image/heic'),
          name: file.name
      }))
    );

      const data = await sendChatMessage(messageText, base64Images);

      setMessages(prev => [...prev, {
        text: data.response,
        isBot: true,
        images: data.images || []
      }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        text: "I am momentarily unable to connect to the gallery network.",
        isBot: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 h-[calc(100vh-100px)] font-serif text-stone-900">

      {/* LAYOUT CHANGE:
        Reverted to standard flex-col for mobile (no reverse).
        We now handle the positioning by manually placing components in the right order.
      */}
      <div className="flex flex-col lg:flex-row h-full bg-[#F9F9F7] border border-stone-200 shadow-sm">

        {/* LEFT SIDE (CHAT COLUMN) */}
        <div className="flex flex-col flex-1 h-full min-w-0 lg:border-r border-stone-200">

            {/* 1. HEADER (Fixed) */}
            <div className="p-4 border-b border-stone-200 shrink-0 flex items-center justify-between bg-white/50 backdrop-blur-sm">
              <div>
                  <h2 className="text-xl font-medium tracking-wide text-stone-900">CREATING YOUR MASTERPIECE</h2>
                  <p className="text-stone-500 text-xs uppercase tracking-widest mt-1 hidden sm:block">
                      Brief session to gather ideas and requirements.
                  </p>
              </div>
              <div>
                 <button
                    onClick={logoutUser}
                    className="text-xs uppercase tracking-widest text-stone-400 hover:text-stone-900 transition-all border-b border-transparent hover:border-stone-900 pb-1"
                    title="Exit Session"
                 >
                    Sign Out
                 </button>
              </div>
            </div>

            {/* 2. MOBILE PRICING STRIP (Fixed - INSERTED HERE) */}
            {/* This sits between the header and the messages, so it never scrolls away */}
            <MobilePricingStrip />

            {/* 3. MESSAGES (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-[#F9F9F7]">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`max-w-[85%] p-5 ${
                    msg.isBot
                      ? "bg-white border border-stone-200 text-stone-800"
                      : "bg-stone-800 text-stone-50"
                  }`}>
                    {msg.images && msg.images.length > 0 && (
                      <div className="flex flex-wrap gap-4 mb-4">
                        {msg.images.map((imgSrc, i) => (
                          <img
                            key={i}
                            src={imgSrc}
                            alt="Uploaded content"
                            className="max-h-64 object-contain border border-stone-100 cursor-pointer"
                            onClick={() => window.open(imgSrc, '_blank')}
                          />
                        ))}
                      </div>
                    )}
                    <p className="whitespace-pre-wrap leading-relaxed text-sm md:text-base font-light">
                        {msg.text}
                    </p>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                   <div className="bg-white p-4 border border-stone-100 flex items-center gap-2">
                    <span className="text-xs text-stone-400 uppercase tracking-widest">Analyzing Composition...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 4. INPUT AREA (Fixed) */}
            <div className="p-6 bg-white border-t border-stone-200 shrink-0">

              {imagePreviews.length > 0 && (
                <div className="flex gap-4 mb-4 overflow-x-auto pb-2">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group shrink-0">
                      <img src={preview} alt="Preview" className="w-20 h-20 object-cover border border-stone-200 grayscale hover:grayscale-0 transition-all" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-stone-800 text-white w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {isProcessingImages && (
                <div className="mb-2 text-xs text-stone-400 uppercase tracking-widest animate-pulse">
                   Processing high-res images...
                </div>
              )}

              <div className="flex items-end gap-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*, .heic, .heif"
                  multiple
                  className="hidden"
                />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="pb-3 text-stone-400 hover:text-stone-900 transition-colors"
                  title="Upload image"
                  disabled={isProcessingImages}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>

                <div className="flex-1">
                  <input
                    type="text"
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={selectedImages.length > 0 ? "Add a description..." : "Type your answer here..."}
                    className="w-full bg-transparent border-b border-stone-300 p-3 focus:outline-none focus:border-stone-800 transition-colors text-stone-900 placeholder-stone-400 font-serif"
                    disabled={isProcessingImages}
                  />
                </div>

                <button
                  onClick={sendMessage}
                  disabled={isLoading || isProcessingImages || (!currentMessage.trim() && selectedImages.length === 0)}
                  className={`pb-3 transition-colors ${
                    isLoading || isProcessingImages || (!currentMessage.trim() && selectedImages.length === 0)
                      ? 'text-stone-300 cursor-not-allowed'
                      : 'text-stone-900 hover:text-stone-600'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
        </div>

        {/* RIGHT SIDE (DESKTOP PRICING) */}
        <DesktopPricingPanel />

      </div>
    </div>
  );
}

export default ChatbotTab;